import React, {
  createContext,
  useContext,
  useState,
  useEffect,
  useCallback,
} from "react";
import { getLoggedInUser, logout as logoutApi } from "../api/api";
import { useNavigate } from "react-router-dom";

interface User {
  User: string;
  User_Profile: string;
  Can_Report_Feedback: boolean;
  Can_Clear_IA_Duplicate_Queue: boolean;
  Can_Use_Copilot: boolean;
}

interface AuthContextType {
  currentUser: string | null | undefined;
  isLoggedIn: boolean;
  userProfile: string;
  canSupplyFeedback: boolean;
  canClearIAQueue: boolean;
  canUseCopilot: boolean;
  isLoading: boolean;

  // Actions
  login: (user: User) => void;
  logout: () => void;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

interface AuthProviderProps {
  children: React.ReactNode;
}

export const AuthProvider: React.FC<AuthProviderProps> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<string | null | undefined>(
    undefined
  );
  const [isLoggedIn, setIsLoggedIn] = useState<boolean>(false);
  const [userProfile, setUserProfile] = useState<string>("");
  const [canSupplyFeedback, setCanSupplyFeedback] = useState<boolean>(false);
  const [canClearIAQueue, setCanClearIAQueue] = useState<boolean>(false);
  const [canUseCopilot, setCanUseCopilot] = useState<boolean>(false);

  const [isLoading, setIsLoading] = useState<boolean>(true);
  const navigate = useNavigate();

  const login = useCallback((user: User) => {
    setCurrentUser(user.User);
    setIsLoggedIn(true);
    setUserProfile(user.User_Profile);
    setCanSupplyFeedback(user.Can_Report_Feedback);
    setCanClearIAQueue(user.Can_Clear_IA_Duplicate_Queue);
    setCanUseCopilot(user.Can_Use_Copilot);
  }, []);

  const logout = useCallback(() => {
    logoutApi()
      .then(() => {
        setCurrentUser("");
        setIsLoggedIn(false);
        setUserProfile("");
        setCanSupplyFeedback(false);
        setCanClearIAQueue(false);
        navigate("/");
      })
      .catch((err) => console.log(err));
  }, []);

  // Initialize authentication state
  useEffect(() => {
    const initializeAuth = async () => {
      if (window.location.pathname.endsWith("/disabled")) {
        setCurrentUser("");
        setIsLoading(false);
        return;
      }

      if (window.location.pathname.search("active-directory") !== -1) {
        setCurrentUser(null);
        setIsLoggedIn(false);
        setIsLoading(false);
        return;
      }

      try {
        const response = await getLoggedInUser();
        setCurrentUser(response.User);
        setIsLoggedIn(true);
        setUserProfile(response.User_Profile);
        setCanSupplyFeedback(response.Can_Report_Feedback);
        setCanClearIAQueue(response.Can_Clear_IA_Duplicate_Queue);
        setCanUseCopilot(response.Can_Use_Copilot);
      } catch (error) {
        setCurrentUser("");
        setIsLoggedIn(false);
      } finally {
        setIsLoading(false);
      }
    };

    if (currentUser === undefined) {
      initializeAuth();
    }
  }, [isLoggedIn]);

  const value: AuthContextType = {
    currentUser,
    isLoggedIn,
    userProfile,
    canSupplyFeedback,
    canClearIAQueue,
    canUseCopilot,
    isLoading,
    login,
    logout,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuth = (): AuthContextType => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within an AuthProvider");
  }
  return context;
};
